import React, { ReactElement, ReactNode, useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { ChevronLeft, Pencil, Trash2 } from 'lucide-react';

import { Button } from '@ui/components/ui/button';
import { Input } from '@ui/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  selectItemsByValue,
} from '@ui/components/ui/select';
import { Loader } from '@ui';

import { cardClass, pageTitleClass } from '@app/constants/styles';
import { membersPath } from '@app/constants/url';
import { useBackNavigation } from '@app/components/core/useBackNavigation';
import { useOrganizationId } from '@app/utils/hooks';
import { Roles } from '@app/utils/types';
import { MemberCountry, MemberSeniority, MembershipStatus, UserEntity } from '@data';
import { useMembership, useRemoveMembership, useUpdateMembership, useUpdateUserStatus } from '@data/hooks/membership';
import { useOrganizationChapters } from '@data/hooks/organization';
import { useOrganizations } from '@data/hooks/organizations';

import { EditSheet } from '@app/components/core/edit-sheet';
import { useIsMobile } from '@ui/hooks/use-mobile';
import { SENIORITY_LEVELS } from '../memberMock';
import ConfirmDeactivateDialog from './ConfirmDeactivateDialog';
import MemberHourlyCost from './MemberHourlyCost';
import MemberProductHours from './MemberProductHours';
import ConfirmEmailChangeDialog from './ConfirmEmailChangeDialog';
import ProductLevelsDialog from './ProductLevelsDialog';
import RemoveMemberDialog from './RemoveMemberDialog';
import { changeKind, keptBeforeSave, profileChange } from './profileChange';
import { useProductLevelChange } from './useProductLevelChange';
import type { ProfileValues } from '../../products/productDetail/levelFromSprint';

interface MemberProps {
  user: UserEntity;
}

const NO_CHAPTER = 'none';
/** The row that means the person has no level recorded, as opposed to one of the three. */
const NO_SENIORITY = '';
const NO_COUNTRY = 'none';
const COUNTRIES = [MemberCountry.belgium, MemberCountry.ukraine];
const MEMBER_NUMBER_MAX = 255;
const DEACTIVATED_PILL_CLASS = 'rounded-full bg-muted px-2.5 py-1 text-xs font-semibold text-muted-foreground';
const ROLES = [Roles.admin, Roles.productManager, Roles.teamMember];

const viewLabel = 'text-xs font-semibold uppercase tracking-wider text-muted-foreground';
const dash = <span className="text-muted-foreground">—</span>;

/** One aligned label/value row, matching the product and client detail pages. */
const DetailRow = ({ label, children }: { label: string; children: ReactNode }): ReactElement => (
  <div className="flex flex-col gap-1 py-4 first:pt-0 last:pb-0 sm:flex-row sm:gap-6">
    <dt className={`${viewLabel} sm:w-44 sm:shrink-0 sm:pt-1.5`}>{label}</dt>
    <dd className="min-w-0 flex-1 text-sm text-foreground">{children}</dd>
  </div>
);

type FormValues = {
  firstName: string;
  lastName: string;
  email: string;
  memberNumber: string;
  country: string;
  // A `Select` reads and writes strings, so the boolean is carried as one and converted on save.
  isContractor: string;
  seniority: string;
  defaultChapter: string;
  role: string;
};

function MemberLoaded({ user, organizationId }: MemberProps & { organizationId: string }): ReactElement {
  const { t } = useTranslation();
  const { membershipId } = useParams<{ membershipId: string }>();
  const navigate = useNavigate();
  // Back goes where the reader came from, and reads as that place — a person is reached from the members
  // list, but also from a product's team and from reporting.
  const { label: backLabel, goBack } = useBackNavigation(membersPath(organizationId), t('actions.backToMembers'));

  const { data: membership, isLoading } = useMembership(membershipId);
  // The signed-in person's role in this organization, from the same source `withAuth` gates routes on.
  // `OrganizationRepresentation` carries no memberships, so it cannot answer this.
  const { data: organizations } = useOrganizations(user.id);
  // Defaults to the chapters the organization currently uses: a retired one is not a default anybody can
  // act on, so it is never offered.
  const { data: chapters } = useOrganizationChapters(organizationId);

  const readerRole = organizations?.items?.find(o => o.id === organizationId)?.role;
  const isAdmin = readerRole === Roles.admin;
  const isSelf = membership?.user?.id === user.id;
  // Exactly who the reports endpoint answers, so the card is never offered where it would 403.
  const canSeeHours = isAdmin || readerRole === Roles.productManager || isSelf;
  // A salary: exactly who its route answers, and not the person themselves.
  const canSeeCost = isAdmin || readerRole === Roles.productManager;

  const updateMembership = useUpdateMembership(membershipId!);
  const { mutate: updateStatus, isPending: statusIsPending } = useUpdateUserStatus();
  const removeMembership = useRemoveMembership();

  // The card reads rather than edits until asked. A form standing permanently open invites changes nobody
  // came to make, and for a product manager — who may change nothing — greyed-out inputs read as broken.
  const [editing, setEditing] = useState(false);
  const isMobile = useIsMobile();
  const [pendingEmail, setPendingEmail] = useState<FormValues | null>(null);
  // A save whose new level or chapter asks which products should follow it.
  const [pendingLevel, setPendingLevel] = useState<FormValues | null>(null);
  const productLevels = useProductLevelChange(organizationId, membershipId!, pendingLevel !== null);
  const [isDeactivating, setIsDeactivating] = useState(false);
  const [isRemoving, setIsRemoving] = useState(false);

  const {
    control,
    handleSubmit,
    reset,
    setError,
    formState: { errors, isDirty, isSubmitting },
  } = useForm<FormValues>({
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      memberNumber: '',
      country: NO_COUNTRY,
      isContractor: 'false',
      seniority: NO_SENIORITY,
      defaultChapter: NO_CHAPTER,
      role: Roles.teamMember,
    },
  });

  const formValuesFor = (m: typeof membership): FormValues => ({
    firstName: m?.user.firstName ?? '',
    lastName: m?.user.lastName ?? '',
    email: m?.user.email ?? '',
    memberNumber: m?.memberNumber ?? '',
    country: m?.country ?? NO_COUNTRY,
    isContractor: m?.isContractor ? 'true' : 'false',
    seniority: m?.seniority ?? NO_SENIORITY,
    // A default pointing at a retired chapter reads as no default rather than showing a dead value.
    defaultChapter: m?.defaultChapter?.isActive ? m.defaultChapter.key : NO_CHAPTER,
    role: m?.role ?? Roles.teamMember,
  });

  useEffect(() => {
    if (membership) reset(formValuesFor(membership));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [membership, reset]);

  const setStatus = (activeStatus: MembershipStatus) =>
    updateStatus({ userId: membership?.user?.id ?? '', activeStatus });

  const save = (values: FormValues, onSaved?: () => void) => {
    updateMembership.mutate(
      {
        firstName: values.firstName,
        lastName: values.lastName,
        email: values.email,
        memberNumber: values.memberNumber === '' ? null : values.memberNumber,
        country: values.country === NO_COUNTRY ? null : (values.country as MemberCountry),
        isContractor: values.isContractor === 'true',
        seniority: values.seniority === NO_SENIORITY ? null : (values.seniority as MemberSeniority),
        defaultChapter: values.defaultChapter === NO_CHAPTER ? null : values.defaultChapter,
        role: values.role,
      },
      {
        onSuccess: () => {
          setPendingEmail(null);
          setPendingLevel(null);
          setEditing(false);
          onSaved?.();
        },
        onError: error => {
          const status = (error as { response?: { status?: number } })?.response?.status;
          setError('root', {
            message: status === 400 ? t('memberDetails.saveRejected') : t('memberDetails.saveFailed'),
          });
        },
      },
    );
  };

  const currentChapter = membership?.defaultChapter?.isActive ? membership.defaultChapter.key : undefined;
  const profileOf = (values: FormValues) => ({
    level: values.seniority === NO_SENIORITY ? undefined : values.seniority,
    chapter: values.defaultChapter === NO_CHAPTER ? undefined : values.defaultChapter,
  });
  const currentProfile = { level: membership?.seniority ?? undefined, chapter: currentChapter };
  const changeOf = (values: FormValues): ProfileValues => profileChange(profileOf(values), currentProfile);

  // Products not yet keeping their own value read the profile, so they keep it before it changes under them.
  const keepThenSave = (values: FormValues, onSaved?: () => void) => {
    const kept = keptBeforeSave(profileOf(values), currentProfile);
    if (!kept) {
      save(values, onSaved);
      return;
    }
    productLevels.keep(kept).then(
      // With no dialog to apply, a product that could not keep its value is still named.
      () => save(values, onSaved ?? (() => productLevels.apply([], {}))),
      () => {
        setPendingLevel(null);
        setError('root', { message: t('memberDetails.saveFailed') });
      },
    );
  };

  // A product keeps the level and chapter it copied in, so a new one asks which products should follow it.
  const proceed = (values: FormValues) => {
    const change = changeOf(values);
    if (change.level !== undefined || change.chapter !== undefined) {
      setPendingEmail(null);
      setPendingLevel(values);
      return;
    }
    keepThenSave(values);
  };

  const onSubmit = (values: FormValues) => {
    // Changing an email changes who can sign into that account, so it is confirmed before it is saved
    // rather than treated like editing a label.
    if (membership && values.email !== membership.user.email) {
      setPendingEmail(values);
      return;
    }
    proceed(values);
  };

  if (isLoading || !membership) return <Loader />;

  const isDeactivated = membership.user.status === MembershipStatus.deactivated;
  const isPending = updateMembership.isPending || isSubmitting || productLevels.isApplying;
  const fullName = `${membership.user.firstName ?? ''} ${membership.user.lastName ?? ''}`.trim();
  const chapterLabel = membership.defaultChapter?.isActive ? membership.defaultChapter.label : undefined;
  const roleItems = selectItemsByValue(ROLES.map(role => ({ value: role, label: t(`roles.${role}`) })));
  const seniorityItems = selectItemsByValue([
    { value: NO_SENIORITY, label: t('noSeniority') },
    ...SENIORITY_LEVELS.map(level => ({ value: level, label: t(`seniorityLevels.${level}`) })),
  ]);
  const countryItems = selectItemsByValue([
    { value: NO_COUNTRY, label: t('memberDetails.noCountry') },
    ...COUNTRIES.map(country => ({ value: country, label: t(`countries.${country}`) })),
  ]);
  const contractorItems = selectItemsByValue([
    { value: 'false', label: t('no') },
    { value: 'true', label: t('yes') },
  ]);
  const pendingChange = pendingLevel ? changeOf(pendingLevel) : {};
  const kind = changeKind(pendingChange);
  const newChapterLabel = chapters?.items?.find(chapter => chapter.key === pendingChange.chapter)?.label ?? '';
  const dialogTitle = t(`memberDetails.productLevels.title${kind}`);
  const dialogBody = t(`memberDetails.productLevels.body${kind}`, {
    name: fullName,
    level: pendingChange.level ? t(`seniorityLevels.${pendingChange.level}`) : '',
    chapter: newChapterLabel,
    from: productLevels.fromLabel,
  });
  const chapterItems = selectItemsByValue([
    { value: NO_CHAPTER, label: t('memberDetails.noDefaultChapter') },
    ...(chapters?.items ?? []).map(chapter => ({ value: chapter.key, label: chapter.label })),
  ]);

  // A phone edits in a sheet (below), leaving the card as it reads.
  const inline = editing && !isMobile;

  const cardActions = (() => {
    if (!isAdmin) return null;
    if (!inline) {
      return (
        <Button type="button" variant="outline" size="sm" onClick={() => setEditing(true)}>
          <Pencil className="size-4" />
          {t('edit')}
        </Button>
      );
    }
    return (
      <div className="flex gap-2">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          disabled={isPending}
          onClick={() => {
            reset(formValuesFor(membership));
            setEditing(false);
          }}
        >
          {t('actions.cancel')}
        </Button>
        <Button type="submit" size="sm" disabled={isPending || !isDirty}>
          {t('actions.save')}
        </Button>
      </div>
    );
  })();

  // Somebody switched off cannot sign in, so what they are permitted to do is moot. A live dropdown there
  // invites a change that means nothing until they are switched back on.
  const permissionsCell = (edit: boolean): ReactNode => {
    if (isDeactivated) return <span className="text-muted-foreground">{t('noPermissions')}</span>;
    if (!edit) return t(`roles.${membership.role}`);
    return (
      <Controller
        name="role"
        control={control}
        render={({ field }) => (
          <Select items={roleItems} value={field.value} onValueChange={field.onChange} disabled={isPending}>
            <SelectTrigger aria-label={t('permissions')} className="w-56 max-w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {ROLES.map(role => (
                <SelectItem key={role} value={role}>
                  {t(`roles.${role}`)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      />
    );
  };

  const rows = (edit: boolean): ReactElement => (
    <dl className="divide-y divide-border">
      <DetailRow label={t('titles.name')}>
        {edit ? (
          <div className="flex flex-col gap-3 sm:flex-row">
            <Controller
              name="firstName"
              control={control}
              rules={{ required: t('memberDetails.required') }}
              render={({ field }) => (
                <div className="flex-1">
                  <Input
                    aria-label={t('memberDetails.firstName')}
                    {...field}
                    disabled={isPending}
                    autoComplete="off"
                    className="bg-popover"
                  />
                  {errors.firstName?.message ? (
                    <p className="mt-1 text-sm text-destructive">{errors.firstName.message}</p>
                  ) : null}
                </div>
              )}
            />
            <Controller
              name="lastName"
              control={control}
              rules={{ required: t('memberDetails.required') }}
              render={({ field }) => (
                <div className="flex-1">
                  <Input
                    aria-label={t('memberDetails.lastName')}
                    {...field}
                    disabled={isPending}
                    autoComplete="off"
                    className="bg-popover"
                  />
                  {errors.lastName?.message ? (
                    <p className="mt-1 text-sm text-destructive">{errors.lastName.message}</p>
                  ) : null}
                </div>
              )}
            />
          </div>
        ) : (
          <span className="font-semibold text-foreground">{fullName || dash}</span>
        )}
      </DetailRow>

      <DetailRow label={t('email')}>
        {edit ? (
          <Controller
            name="email"
            control={control}
            rules={{
              required: t('memberDetails.required'),
              pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: t('memberDetails.emailInvalid') },
            }}
            render={({ field }) => (
              <div className="max-w-[22rem]">
                <Input
                  aria-label={t('memberDetails.email')}
                  type="email"
                  {...field}
                  disabled={isPending}
                  autoComplete="off"
                  className="bg-popover"
                  aria-describedby="member-email-hint"
                />
                <p id="member-email-hint" className="mt-1 text-xs text-muted-foreground">
                  {t('memberDetails.emailHint')}
                </p>
                {errors.email?.message ? <p className="mt-1 text-sm text-destructive">{errors.email.message}</p> : null}
              </div>
            )}
          />
        ) : (
          membership.user.email || dash
        )}
      </DetailRow>

      <DetailRow label={t('employeeNumber')}>
        {edit ? (
          <Controller
            name="memberNumber"
            control={control}
            rules={{
              maxLength: {
                value: MEMBER_NUMBER_MAX,
                message: t('memberDetails.maxLength', { max: MEMBER_NUMBER_MAX }),
              },
            }}
            render={({ field }) => (
              <div className="max-w-[12rem]">
                <Input
                  aria-label={t('employeeNumber')}
                  {...field}
                  disabled={isPending}
                  autoComplete="off"
                  className="bg-popover"
                />
                {errors.memberNumber?.message ? (
                  <p className="mt-1 text-sm text-destructive">{errors.memberNumber.message}</p>
                ) : null}
              </div>
            )}
          />
        ) : (
          membership.memberNumber || dash
        )}
      </DetailRow>

      <DetailRow label={t('defaultChapter')}>
        {edit ? (
          <Controller
            name="defaultChapter"
            control={control}
            render={({ field }) => (
              <Select items={chapterItems} value={field.value} onValueChange={field.onChange} disabled={isPending}>
                <SelectTrigger aria-label={t('defaultChapter')} className="w-56 max-w-full">
                  <SelectValue placeholder={t('memberDetails.noDefaultChapter')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={NO_CHAPTER}>{t('memberDetails.noDefaultChapter')}</SelectItem>
                  {chapters?.items?.map(chapter => (
                    <SelectItem key={chapter.id} value={chapter.key}>
                      {chapter.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          />
        ) : (
          chapterLabel || dash
        )}
      </DetailRow>

      <DetailRow label={t('seniority')}>
        {edit ? (
          <Controller
            name="seniority"
            control={control}
            render={({ field }) => (
              <Select
                items={seniorityItems}
                value={field.value}
                onValueChange={level => field.onChange(level ?? NO_SENIORITY)}
                disabled={isPending}
              >
                <SelectTrigger aria-label={t('seniority')} className="w-56 max-w-full">
                  <SelectValue placeholder={t('noSeniority')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={NO_SENIORITY}>{t('noSeniority')}</SelectItem>
                  {SENIORITY_LEVELS.map(level => (
                    <SelectItem key={level} value={level}>
                      {t(`seniorityLevels.${level}`)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          />
        ) : (
          (membership.seniority && t(`seniorityLevels.${membership.seniority}`)) || dash
        )}
      </DetailRow>

      <DetailRow label={t('country')}>
        {edit ? (
          <Controller
            name="country"
            control={control}
            render={({ field }) => (
              <Select items={countryItems} value={field.value} onValueChange={field.onChange} disabled={isPending}>
                <SelectTrigger aria-label={t('country')} className="w-56 max-w-full">
                  <SelectValue placeholder={t('memberDetails.noCountry')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={NO_COUNTRY}>{t('memberDetails.noCountry')}</SelectItem>
                  {COUNTRIES.map(country => (
                    <SelectItem key={country} value={country}>
                      {t(`countries.${country}`)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          />
        ) : (
          (membership.country && t(`countries.${membership.country}`)) || dash
        )}
      </DetailRow>

      <DetailRow label={t('contractor')}>
        {edit ? (
          <Controller
            name="isContractor"
            control={control}
            render={({ field }) => (
              <Select items={contractorItems} value={field.value} onValueChange={field.onChange} disabled={isPending}>
                <SelectTrigger aria-label={t('contractor')} className="w-56 max-w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="false">{t('no')}</SelectItem>
                  <SelectItem value="true">{t('yes')}</SelectItem>
                </SelectContent>
              </Select>
            )}
          />
        ) : (
          t(membership.isContractor ? 'yes' : 'no')
        )}
      </DetailRow>

      <DetailRow label={t('status')}>
        {/* Not offered at all on your own page, rather than offered and refused: the server rejects it
            because a deactivated account cannot sign back in to undo it, so there is nothing to click.
            The hint under the card says why. */}
        {edit && !isSelf ? (
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={statusIsPending}
            // Only one direction asks. Handing an account back is harmless and undoable; taking it
            // away locks the person out of the app they would undo it in.
            onClick={() => (isDeactivated ? setStatus(MembershipStatus.active) : setIsDeactivating(true))}
          >
            {isDeactivated ? t('actions.activate') : t('actions.deactivate')}
          </Button>
        ) : (
          <span
            className={
              isDeactivated
                ? DEACTIVATED_PILL_CLASS
                : 'rounded-full bg-emerald-100 px-2.5 py-1 text-xs font-semibold text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200'
            }
          >
            {isDeactivated ? t('tabs.deactivated') : t('tabs.active')}
          </span>
        )}
      </DetailRow>

      <DetailRow label={t('permissions')}>{permissionsCell(edit)}</DetailRow>
    </dl>
  );

  return (
    <div className="w-full">
      <button
        type="button"
        onClick={goBack}
        className="mb-4 inline-flex items-center gap-1.5 text-sm text-muted-foreground transition-colors hover:text-foreground"
      >
        <ChevronLeft className="size-4" />
        {backLabel}
      </button>

      <div className="mb-6 flex items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <h1 className={pageTitleClass}>{fullName}</h1>
          {/* Next to the name, so who you are looking at and whether they are switched off read as one thing. */}
          {isDeactivated ? <span className={DEACTIVATED_PILL_CLASS}>{t('tabs.deactivated')}</span> : null}
        </div>
        {/* Offered only while the server says it is allowed — it answers `canBeRemoved` with the same
            condition the delete route enforces, so this never offers a control that gets refused. */}
        {isAdmin && membership.canBeRemoved && !isSelf ? (
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="shrink-0 bg-card text-destructive"
            onClick={() => setIsRemoving(true)}
          >
            <Trash2 className="size-4" />
            {t('actions.remove')}
          </Button>
        ) : null}
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className={`${cardClass} mb-6`}>
        <div className="mb-5 flex items-center justify-between gap-4">
          <h2 className="text-lg font-bold text-foreground">{t('titles.memberDetails')}</h2>
          {cardActions}
        </div>

        {rows(inline)}

        {inline && errors.root?.message ? <p className="mt-4 text-sm text-destructive">{errors.root.message}</p> : null}
        {!isAdmin ? <p className="mt-4 text-xs text-muted-foreground">{t('adminOnlyStatusPermissions')}</p> : null}
        {inline && isSelf ? (
          <p className="mt-4 text-xs text-muted-foreground">{t('cannotDeactivateSelfHint')}</p>
        ) : null}
      </form>
      <EditSheet
        open={editing && isMobile}
        title={t('titles.memberDetails')}
        onCancel={() => {
          reset(formValuesFor(membership));
          setEditing(false);
        }}
        onSubmit={handleSubmit(onSubmit)}
        isPending={isPending}
        canSave={isDirty}
        error={errors.root?.message}
      >
        {rows(true)}
        {isSelf ? <p className="mt-4 text-xs text-muted-foreground">{t('cannotDeactivateSelfHint')}</p> : null}
      </EditSheet>

      {canSeeCost ? <MemberHourlyCost membershipId={membership.id} /> : null}

      {canSeeHours ? <MemberProductHours organizationId={organizationId} userId={membership.user?.id} /> : null}

      <ConfirmDeactivateDialog
        open={isDeactivating}
        name={fullName}
        isPending={statusIsPending}
        onCancel={() => setIsDeactivating(false)}
        onConfirm={() => {
          setStatus(MembershipStatus.deactivated);
          setIsDeactivating(false);
        }}
      />

      <RemoveMemberDialog
        open={isRemoving}
        name={fullName}
        isPending={removeMembership.isPending}
        onCancel={() => setIsRemoving(false)}
        onConfirm={() =>
          removeMembership.mutate(membership.id, {
            onSuccess: () => navigate(membersPath(organizationId)),
          })
        }
      />

      <ConfirmEmailChangeDialog
        open={pendingEmail !== null}
        currentEmail={membership.user.email ?? ''}
        newEmail={pendingEmail?.email ?? ''}
        isPending={isPending}
        onCancel={() => setPendingEmail(null)}
        onConfirm={() => pendingEmail && proceed(pendingEmail)}
      />
      <ProductLevelsDialog
        open={pendingLevel !== null}
        title={dialogTitle}
        body={dialogBody}
        products={productLevels.products}
        isLoading={productLevels.isLoading}
        isPending={isPending || productLevels.isApplying}
        onCancel={() => setPendingLevel(null)}
        onConfirm={productIds => {
          if (!pendingLevel) return;
          const next = changeOf(pendingLevel);
          keepThenSave(pendingLevel, () => productLevels.apply(productIds, next));
        }}
      />
    </div>
  );
}

const Member = ({ user }: MemberProps): ReactElement => {
  const organizationId = useOrganizationId();
  if (!organizationId) return <Loader />;
  return <MemberLoaded user={user} organizationId={organizationId} />;
};

export default Member;
